import type { z } from 'zod';

/**
 * 라우트 계약 한 건.
 *
 * service 는 이 값으로 라우트를 등록하고(access 로 미들웨어를 붙인다), web 은 같은 값으로 호출한다.
 * Hono RPC(`hc`)를 쓰지 않는 이유 — 라우트가 ~58개를 넘으면 TS2589 로 web 타입 검사가 죽는다
 * (nmstory D133 외 4회). 호출마다 계약 하나만 타입을 인스턴스화하므로 그 한계가 없다.
 */

export type HttpMethod = 'get' | 'post' | 'patch' | 'put' | 'delete';

/**
 * - public    로그인 전
 * - session   로그인만 필요 (내 정보·로그아웃·비밀번호)
 * - scoped    고객 데이터 조회. repository 가 actor.brandIds 로 거른다. 범위 밖은 404
 * - admin     쓰기·전역 리소스. 비-admin 은 403
 * - member    사용자 계약(내 브랜드·소속·초대 /api/account/brands/*) — 사용자만(운영자 403). 범위는 **내 소속**이다
 *             (승인 대기·결제 대기 포함 — 리포트 범위와 다르다). brand/service.ts 의 `myMembership` 이 거르고
 *             범위 밖은 404, 소유자 전용 쓰기는 403 (261013-account-brand-membership)
 * - payer     결제자 계약(/api/billing/*) — 사용자만(운영자 403). 범위는 **결제 범위**로 거른다: 승인된 브랜드 +
 *             결제 대기(confirmed) 브랜드. 리포트 범위(`brandIds`)와 다르다 —
 *             billing/service.ts 의 `payerScope` 가 계산하고 범위 밖은 404 (261011-subscription-billing-payup)
 * - publisher /internal/publish/* — 산출물을 미는 쪽(별개 프로젝트·CI)의 전용 베어러 토큰.
 *             고객 데이터를 **쓴다.** 운영자·워커 자격증명과 권한이 섞이지 않게 값을 따로 둔다.
 *             ★ **읽기는 "누구에게 쓸지"를 알아내는 최소한만** 열린다 — 지금은 고객 조회
 *             (`{brandId, name}` 둘뿐, 정확 일치 1건) 하나다(2026-10-02 · 261002-review-brand-meta).
 *             토큰 사본이 레포에 커밋돼 있으므로 **읽기를 늘리면 레포 접근이 곧 그 조회 권한**이 된다 —
 *             질문·샘플·지표는 열지 않는다
 * - agent     /internal/agent/* — GEO 개선 에이전트 토큰(261023-geo-watch-agent). 고객 지표·샘플·프로필을 **읽고**,
 *             쓰기는 인터뷰 케이스 표 하나다. 토큰은 레포에 두지 않는다(루트 `.env`·Secret Manager)
 * - partner   /internal/partner/* — 파트너(라이브애드) 서버의 시크릿(261027-partner-link). 구독 여부만 읽고,
 *             쓰기는 연결(`partner_link`) 만들기 하나다. 시크릿은 레포에 두지 않는다(루트 `.env`·Secret Manager)
 * - video     /internal/video/* — 영상 생산 PC(video 앱)의 토큰(261103-video-production-plan). 영상 계획 행만 읽고,
 *             쓰기는 계획 상태(가져감·예약됨·실패·되돌림) 하나다. 토큰은 레포에 두지 않는다(루트 `.env`·Secret Manager)
 * - video     /internal/video/* — 영상 생산 PC(video 앱)의 토큰(261103-video-production-plan). 영상 계획 행만 읽고,
 *             쓰기는 계획 상태(가져감·예약됨·실패·되돌림) 하나다. 토큰은 레포에 두지 않는다(루트 `.env`·Secret Manager)
 * - worker    /internal/worker/* — 워커 자격증명
 * - enroll    /internal/enroll — 등록 토큰. 아직 워커 자격증명이 없는 기계가 자기 것을 발급받는 한 곳
 * - scheduler /internal/scheduler/* — Cloud Scheduler OIDC
 *
 * ★ 값을 추가하면 service/src/http.ts 의 enforceAccess 가 컴파일 오류로 알려준다. 거기서 검사를 붙인다
 */
export type Access =
  | 'public'
  | 'session'
  | 'scoped'
  | 'admin'
  | 'member'
  | 'payer'
  | 'publisher'
  | 'agent'
  | 'partner'
  | 'video'
  | 'worker'
  | 'enroll'
  | 'scheduler';

export interface RouteDef {
  method: HttpMethod;
  /** OpenAPI 표기: `/api/queries/{id}` */
  path: `/${string}`;
  access: Access;
  summary: string;
  params?: z.ZodObject;
  query?: z.ZodObject;
  body?: z.ZodType;
  response: z.ZodType;
}

export const defineRoute = <const T extends RouteDef>(def: T): T => def;

type InferOr<S, Fallback> = S extends z.ZodType ? z.infer<S> : Fallback;
export type RouteParams<T extends RouteDef> = InferOr<T['params'], undefined>;
export type RouteQuery<T extends RouteDef> = InferOr<T['query'], undefined>;
export type RouteBody<T extends RouteDef> = InferOr<T['body'], undefined>;
export type RouteResponse<T extends RouteDef> = z.infer<T['response']>;

/** `/api/queries/{id}` + `{ id: 'x' }` → `/api/queries/x` */
export function buildPath(path: string, params?: Record<string, string>): string {
  return path.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params?.[key];
    if (value === undefined) throw new Error(`경로 파라미터 ${key} 가 없다: ${path}`);
    return encodeURIComponent(value);
  });
}

/** 에러 응답 봉투 — 모든 4xx·5xx 가 이 모양이다 */
export interface ErrorEnvelope {
  error: { code: string; message: string };
}
